import {
  type Answers,
  type QuestionKey,
  createInitialAnswers,
} from './constants';
import { useDiagnosisPostalCodeQuestion } from './useDiagnosisPostalCodeQuestion';

export const useDiagnosisQuestions = (initialAnswers?: Answers) => {
  const { isExclusive } = useExclusive();

  const answers = ref(initialAnswers ?? createInitialAnswers());

  const { addressOptions } = useDiagnosisPostalCodeQuestion(
    () => answers.value.postalCode
  );
  watch(addressOptions, (options) => {
    answers.value.address = null;
    if (options.length === 0) {
      return;
    }
    if (options.length === 1) {
      answers.value.address = options[0].content;
      return;
    }
  });

  const ALWAYS_ENABLED = true;
  const enabledQuestions = computed<Record<QuestionKey, boolean>>(() => ({
    gender: ALWAYS_ENABLED,
    jobId: ALWAYS_ENABLED,
    postalCode: ALWAYS_ENABLED,
    address:
      answers.value.postalCode !== null && addressOptions.value.length > 1,
    birthday: ALWAYS_ENABLED,
    savings: ALWAYS_ENABLED,
    annualIncome: ALWAYS_ENABLED,
    monthlySavings: ALWAYS_ENABLED,
    investment: ALWAYS_ENABLED,
    hasPartner: ALWAYS_ENABLED,
    partnerBirthday: answers.value.hasPartner === true,
    partnerAnnualIncome: answers.value.hasPartner === true,
    hasChild: ALWAYS_ENABLED,
    countOfChildren: answers.value.hasChild === true,
    firstChildBirthDay:
      answers.value.hasChild === true &&
      (answers.value.countOfChildren ?? 0) >= 1,
    secondChildBirthDay:
      answers.value.hasChild === true &&
      (answers.value.countOfChildren ?? 0) >= 2,
    thirdChildBirthDay:
      answers.value.hasChild === true &&
      (answers.value.countOfChildren ?? 0) >= 3,
    fourthChildBirthDay:
      answers.value.hasChild === true &&
      (answers.value.countOfChildren ?? 0) >= 4,
    fifthChildBirthDay:
      answers.value.hasChild === true &&
      (answers.value.countOfChildren ?? 0) >= 5,
    firstChildGender:
      answers.value.hasChild === true &&
      (answers.value.countOfChildren ?? 0) >= 1,
    secondChildGender:
      answers.value.hasChild === true &&
      (answers.value.countOfChildren ?? 0) >= 2,
    thirdChildGender:
      answers.value.hasChild === true &&
      (answers.value.countOfChildren ?? 0) >= 3,
    fourthChildGender:
      answers.value.hasChild === true &&
      (answers.value.countOfChildren ?? 0) >= 4,
    fifthChildGender:
      answers.value.hasChild === true &&
      (answers.value.countOfChildren ?? 0) >= 5,
    monthlyRentCost: ALWAYS_ENABLED,
    loanBalance: ALWAYS_ENABLED,
    monthlyInsurance: ALWAYS_ENABLED,
    thinksToughTimesBringOpportunity: ALWAYS_ENABLED,
    thinksWhatDoneIsDone: ALWAYS_ENABLED,
    isInterestedInNisaOrIdeco: ALWAYS_ENABLED,
    hasGoalInFuture: ALWAYS_ENABLED,
    takesThreeOrMoreLessons: ALWAYS_ENABLED,
    followsTheLatestTrends: ALWAYS_ENABLED,
    karteTopics: !isExclusive.value,
    karteComment: !isExclusive.value,
  }));

  const resetAnswers = () => {
    answers.value = createInitialAnswers();
  };

  // 回答不要のaddressを除外
  // addressの扱いが変わったらここも修正すること
  const answeredQuestions = computed(() =>
    Object.entries(answers.value).filter(
      ([key, value]) => key !== 'address' && value !== null
    )
  );

  const numberOfQuestionsNeedToAnswer = computed(
    () =>
      Object.entries(enabledQuestions.value).filter(
        ([_, value]) => value === true
      ).length
  );

  return {
    answers,
    answeredQuestions,
    numberOfQuestionsNeedToAnswer,
    enabledQuestions,
    addressOptions,
    resetAnswers,
  };
};
